import { useState, useEffect } from 'react'
import { featureFlagsApi, type ApiFeatureFlag } from '../utils/api'

// ── §81 Feature Flags Admin Panel ─────────────────────────────────────────────

export function FeatureFlagsPanel({ onClose }: { onClose: () => void }) {
  const [flags, setFlags] = useState<ApiFeatureFlag[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [newKey, setNewKey] = useState('')
  const [newDesc, setNewDesc] = useState('')
  const [adding, setAdding] = useState(false)

  useEffect(() => {
    featureFlagsApi.adminList()
      .then(list => setFlags(list))
      .catch(() => setError('Нет доступа или ошибка загрузки'))
      .finally(() => setLoading(false))
  }, [])

  async function toggle(flag: ApiFeatureFlag) {
    setSaving(flag.key)
    try {
      const updated = await featureFlagsApi.upsert(flag.key, { enabled: !flag.enabled })
      setFlags(prev => prev.map(f => f.key === flag.key ? updated : f))
    } catch { setError('Ошибка сохранения') } finally { setSaving(null) }
  }

  async function updateRollout(flag: ApiFeatureFlag, pct: number) {
    setSaving(flag.key)
    try {
      const updated = await featureFlagsApi.upsert(flag.key, { rolloutPct: pct })
      setFlags(prev => prev.map(f => f.key === flag.key ? updated : f))
    } catch { setError('Ошибка сохранения') } finally { setSaving(null) }
  }

  async function addFlag() {
    if (!newKey.trim()) return
    setAdding(true)
    try {
      const created = await featureFlagsApi.upsert(newKey.trim(), { enabled: false, rolloutPct: 100, description: newDesc.trim() || undefined })
      setFlags(prev => [...prev, created])
      setNewKey(''); setNewDesc('')
    } catch { setError('Ошибка создания флага') } finally { setAdding(false) }
  }

  async function removeFlag(key: string) {
    if (!confirm(`Удалить флаг "${key}"?`)) return
    setSaving(key)
    try {
      await featureFlagsApi.remove(key)
      setFlags(prev => prev.filter(f => f.key !== key))
    } catch { setError('Ошибка удаления') } finally { setSaving(null) }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9900, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: 'var(--bg-1)', borderRadius: 16, width: '100%', maxWidth: 700, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-primary)' }}>🚩 Feature Flags</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Управление флагами функциональности</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--text-muted)', cursor: 'pointer' }}>×</button>
        </div>

        {error && <div style={{ margin: '12px 24px 0', padding: '8px 12px', background: '#fee2e2', color: '#991b1b', borderRadius: 8, fontSize: 12 }}>{error}</div>}

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Загрузка...</div>
          ) : flags.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Флаги не найдены</div>
          ) : flags.map(flag => (
            <div key={flag.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: 'var(--bg-2)', borderRadius: 10, border: '1px solid var(--border)' }}>
              {/* Toggle */}
              <button
                onClick={() => void toggle(flag)}
                disabled={saving === flag.key}
                style={{
                  width: 40, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer',
                  background: flag.enabled ? '#22c55e' : '#d1d5db',
                  position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                }}
              >
                <span style={{
                  position: 'absolute', top: 2, left: flag.enabled ? 20 : 2,
                  width: 18, height: 18, borderRadius: '50%', background: '#fff',
                  transition: 'left 0.2s', display: 'block',
                }} />
              </button>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'monospace' }}>{flag.key}</div>
                {flag.description && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{flag.description}</div>}
              </div>

              {/* Rollout % */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                <input
                  type="number"
                  min={0} max={100}
                  value={flag.rolloutPct}
                  onChange={e => void updateRollout(flag, Number(e.target.value))}
                  disabled={saving === flag.key}
                  style={{ width: 52, padding: '3px 6px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, background: 'var(--bg-1)', color: 'var(--text-primary)', textAlign: 'right' }}
                />
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>%</span>
              </div>

              {/* Status badge */}
              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 8, flexShrink: 0,
                background: flag.enabled ? '#dcfce7' : '#f3f4f6',
                color: flag.enabled ? '#16a34a' : '#6b7280',
              }}>
                {flag.enabled ? 'ON' : 'OFF'}
              </span>

              <button onClick={() => void removeFlag(flag.key)} disabled={saving === flag.key}
                style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 14, padding: '2px 6px', flexShrink: 0 }}>
                🗑
              </button>
            </div>
          ))}
        </div>

        {/* Add new flag */}
        <div style={{ padding: '12px 24px 20px', borderTop: '1px solid var(--border)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Добавить флаг</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              value={newKey}
              onChange={e => setNewKey(e.target.value)}
              placeholder="feature.key"
              style={{ flex: 1, padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, background: 'var(--bg-2)', color: 'var(--text-primary)', fontFamily: 'monospace' }}
            />
            <input
              value={newDesc}
              onChange={e => setNewDesc(e.target.value)}
              placeholder="Описание (необязательно)"
              style={{ flex: 2, padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, background: 'var(--bg-2)', color: 'var(--text-primary)' }}
            />
            <button
              onClick={() => void addFlag()}
              disabled={adding || !newKey.trim()}
              style={{ padding: '8px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: adding ? 'not-allowed' : 'pointer', opacity: adding || !newKey.trim() ? 0.6 : 1 }}
            >
              {adding ? '...' : '+ Добавить'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
